import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { ReactNode } from 'react';

import { useGlobal } from '~/hooks/useGlobal';

import Icon from '../Icon/Icon';
import { IconTypes } from '../Icon/IconTypes';

import styles from './Panel.module.scss';

type Props = {
  onClick?: () => void;
  clickOnlyOnIcon?: boolean;
  children: ReactNode | ReactNode[];
  classNames?: string;
  contentClassNames?: string;
  icon?: IconTypes;
  iconSize?: number;
  iconClassName?: string;
  isCompact?: boolean;
  hasBorder?: boolean;
  additionalContent?: ReactNode;
};

const Panel = ({
  onClick,
  clickOnlyOnIcon = false,
  classNames,
  children,
  contentClassNames,
  icon,
  iconSize,
  iconClassName,
  isCompact,
  hasBorder = true,
  additionalContent,
}: Props) => {
  const isClickable = !!onClick;
  const { isMobile } = useGlobal();

  const mainInner = (
    <>
      <div className={styles.contentContainer}>
        <div className={styles.content}>{children}</div>
      </div>
      {isClickable && (
        <div className={styles.chevronContainer}>
          <div
            className={classes([
              styles.chevron,
              clickOnlyOnIcon && styles.chevronButton,
            ])}
            onClick={clickOnlyOnIcon ? onClick : undefined}
          >
            <Icon
              type="chevron"
              size={isMobile ? 20 : 24}
              className={styles.icon}
            />
          </div>
        </div>
      )}
    </>
  );

  return (
    <div
      className={classes([
        styles.panel,
        isCompact && styles.compact,
        !hasBorder && styles.noBorder,
        classNames,
      ])}
    >
      {icon && (
        <div className={classes([styles.iconContainer, iconClassName])}>
          <Icon
            type={icon}
            size={iconSize || (isMobile ? 20 : 24)}
            className={styles.icon}
          />
        </div>
      )}
      <div className={classes([styles.contentBlock, contentClassNames])}>
        {isClickable ? (
          <button
            className={classes([
              styles.clickable,
              clickOnlyOnIcon && styles.clickableIcon,
            ])}
            onClick={clickOnlyOnIcon ? undefined : onClick}
          >
            {mainInner}
          </button>
        ) : (
          <div className={styles.clickable}>{mainInner}</div>
        )}
        {!isMobile && additionalContent && (
          <div className={styles.additionalContentContainer}>
            <div className={styles.additionalContent}>{additionalContent}</div>
          </div>
        )}
      </div>
      {isMobile && additionalContent && (
        <div className={styles.additionalContentContainer}>
          <div className={styles.additionalContent}>{additionalContent}</div>
        </div>
      )}
    </div>
  );
};

export default observer(Panel);
